import { useEffect } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useNavigate } from 'react-router';
import { TelemetryPageTracker } from '@lunacast/lib-telemetry';
import {
  FloatingAreaHost,
  FloatingAreaProvider,
} from '@lunacast/ui-primitives';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { registerSessionExpiredHandler } from './lib/api';
import { telemetryInitOptions } from './lib/telemetry';
import { userKeys } from './queries/user/keys';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60,
    },
  },
});

const SITE_NAME = 'lunacast 관리자';

/** 세션 만료 시 full reload 없이 /login으로 client navigate한다. */
function SessionExpiredBridge() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    registerSessionExpiredHandler(() => {
      qc.setQueryData(userKeys.profile.queryKey, null);
      qc.removeQueries();
      navigate('/login', { replace: true });
    });
    return () => registerSessionExpiredHandler(null);
  }, [navigate, qc]);

  return null;
}

export default function Root() {
  return (
    <html lang="ko">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        <title>{SITE_NAME}</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={SITE_NAME} />
        <Meta />
        <Links />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <FloatingAreaProvider>
            <SessionExpiredBridge />
            <TelemetryPageTracker initOptions={telemetryInitOptions} />
            <Outlet />
            <FloatingAreaHost />
            <Toaster
              position="bottom-center"
              richColors
              offset={{ bottom: 'max(1rem, env(safe-area-inset-bottom))' }}
              mobileOffset={{ bottom: 'max(1rem, env(safe-area-inset-bottom))' }}
            />
          </FloatingAreaProvider>
        </QueryClientProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}
